import { merge } from "lodash";
import { thor, MediaType } from "../../typings/thor";
import { ApiErrorMessage, Enums, Events, MultiSelectType } from "../../thor.enums";
import { ChangeCategoryDialogController } from "./change-category-dialog.controller";
import { TogglePremiumDialogController } from "./toggle-premium-dialog.controller";
import { isSaleInLessThan48hours } from "../../utils/sale";
import { RecapPremiumDialogController } from "./recap-premium-dialog.controller";
import { getOrderNbr } from "../../utils/sale-item";

interface MultiSelectActions {
    canBeCategorized: boolean;
    canBeHidden: boolean;
    canBeUnHidden: boolean;
    canBeSuppressed: boolean;
    canBeUnSuppressed: boolean;
    canBeMerged: boolean;
}

export class MultipleSelect {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$mdMedia",
        "$q",
        "$rootScope",
        "$translate",
        "absenteeBidsService",
        "configService",
        "categoryService",
        "itemService",
        "toaster",
        "utilsService"
    ];
    public elems: thor.ItemGet[] = [];
    public isBusy: boolean = false;
    public elemType: MultiSelectType;
    public possibleActions: MultiSelectActions;

    private elemIds = [];

    private $t;

    constructor(
        private $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        private $mdMedia: ng.material.IMedia,
        private $q: ng.IQService,
        private $rootScope: ng.IRootScopeService,
        $translate: ng.translate.ITranslateService,
        private absenteeBidsService,
        private configService,
        private categoryService,
        private itemService,
        private toaster,
        private utils
    ) {
        this.$t = $translate.instant;

        $rootScope.$on("$stateChangeSuccess", () => this.cancel());
    }

    public cancel() {
        this.elems = [];
        this.elemIds = [];
        this.elemType = null;
    }

    public changeCategory($event) {
        let concernedItems = this.elems.filter(i => !this.isSalePublished() || this.isItemCreated(i));

        this.$mdDialog
            .show({
                targetEvent: $event,
                template: require("./change-category-dialog.html"),
                fullscreen: this.$mdMedia("xs"),
                locals: {
                    nbElemSelected: concernedItems.length
                },
                controller: ChangeCategoryDialogController,
                controllerAs: "changeCategoryCtrl"
            })
            .then(selectedCategory => {
                if (selectedCategory) {
                    concernedItems = concernedItems.reduce((acc, elem) => {
                        if (elem.category.id !== selectedCategory.id) {
                            merge(elem.category, selectedCategory);
                            acc.push(elem);
                        }
                        return acc;
                    }, []);
                    this._patchItems(concernedItems, { category_id: selectedCategory.id });
                }
            });
    }

    public isSelected(itemId) {
        return this.elemIds.indexOf(itemId) > -1;
    }

    public merge() {
        const concernedItems = this.elems.filter(i => !this.isSalePublished() || this.isItemCreated(i));
        let htmlContent = `<p>${this.$t("vous_allez_fusionner_n_lots", [concernedItems.length])}<p>`;
        let totalMediasCount = 0;
        concernedItems.forEach(elem => (totalMediasCount += elem.media_count));

        const extraMediasCount = totalMediasCount - this.configService.items.maxImages;
        if (extraMediasCount > 0) {
            htmlContent += `<p>${this.$t("la_limite_d_images_par_lot_n_est_depassee", {
                N: this.configService.items.maxImages
            })}<p>`;

            htmlContent += `<p><b>${this.$t("x_images_perdues", { N: extraMediasCount })}</b></p>`;
        }

        const confirm = this.$mdDialog
            .confirm()
            .title(this.$t("fusion_de_lots"))
            .htmlContent(htmlContent)
            .ariaLabel(this.$t("confirmation_de_fusion_de_lots"))
            .ok(this.$t("fusionner_les_lots"))
            .cancel(this.$t("fermer"));

        this.$mdDialog.show(confirm).then(() => {
            this.isBusy = true;

            this.$q
                .series(concernedItems, this._fetchAllItemMedias.bind(this))
                .then(() => this._mergeItems(concernedItems))
                .catch(() => this.toaster.show(this.$t("une_erreur_est_survenue_pendant_la_fusion"), true))
                .finally(() => (this.isBusy = false));
        });
    }

    public toggle(item, type: MultiSelectType, selectOnly = false) {
        this.elemType = type;
        const idx = this.elemIds.indexOf(item.id);

        if (idx > -1) {
            if (!selectOnly) {
                this.elems.splice(idx, 1);
                this.elemIds.splice(idx, 1);

                if (this.elems.length === 0) {
                    this.elemType = null;
                }
            }
        } else {
            this.elems.push(item);
            this.elemIds.push(item.id);
        }
    }

    public restoreItems() {
        const concernedItems = this.elems.filter(e => e.states?.suppressed);
        const confirm = this.$mdDialog
            .confirm()
            .title(this.$t("confirmation_d_annulation_de_suppression"))
            .textContent(this.$t("vous_allez_annuler_la_suppression_de_n_lots", [concernedItems.length]))
            .ok(this.$t("annuler_la_suppression"))
            .cancel(this.$t("fermer"));

        this.$mdDialog.show(confirm).then(() => this._patchItems(concernedItems, { states: { suppressed: false } }));
    }

    public selectAll() {
        this.$rootScope.$broadcast(Events.MultiSelectAll);
    }

    public selectAllOnline() {
        this.$rootScope.$broadcast(Events.MultiSelectAllOnline);
    }

    public hideItems() {
        const itemsWithOrder = [];
        let itemHasSecretOrder: boolean;

        const concernedItems = this.elems.filter((item: thor.ItemGet) => {
            const itemOrder = this.absenteeBidsService.getAbsenteeBidsCountForItem(item.id);

            if (item.secretOrderCount > 0) {
                itemHasSecretOrder = true;
            }
            if (itemOrder) {
                itemsWithOrder.push(itemOrder);
            }
            return !item.states?.hidden && this.isSalePublished() && !this.isItemCreated(item);
        });

        const hasSecret = { SECRETS: itemHasSecretOrder ? this.$t("secrets") : "" };
        let title = this.$t("retirer_les_lots_des_encheres");
        let textContent = this.$t("vous_allez_retirer_n_lots", [concernedItems.length]);
        let ok = this.$t("retirer");
        let cancel = this.$t("fermer");

        if (itemsWithOrder.length || itemHasSecretOrder) {
            title = this.$t("suppression_des_ordres_d_achat", hasSecret);
            textContent = this.$t("attention_cette_action_entrainera_la_suppression_des_ordres_d_achat", hasSecret);
            ok = this.$t("oui");
            cancel = this.$t("non");
        }

        this.$mdDialog
            .show(this.$mdDialog.confirm().title(title).textContent(textContent).ok(ok).cancel(cancel))
            .then(() => this._patchItems(concernedItems, { states: { hidden: true } }));
    }

    public unhideItems() {
        const concernedItems = this.elems.filter(e => e.states?.suppressed);
        const confirm = this.$mdDialog
            .confirm()
            .title(this.$t("annuler_le_retrait_des_encheres"))
            .textContent(this.$t("vous_allez_annuler_le_retrait_de_n_lots", [concernedItems.length]))
            .ok(this.$t("annuler_le_retrait"))
            .cancel(this.$t("fermer"));

        this.$mdDialog.show(confirm).then(() => this._patchItems(concernedItems, { states: { hidden: false } }));
    }

    public suppressItems() {
        const itemsWithOrder = [];
        let itemHasSecretOrder: boolean;

        const concernedItems = this.elems.filter((item: thor.ItemGet) => {
            const itemOrder = this.absenteeBidsService.getAbsenteeBidsCountForItem(item.id);

            if (item.secretOrderCount > 0) {
                itemHasSecretOrder = true;
            }
            if (itemOrder) {
                itemsWithOrder.push(itemOrder);
            }
            return !item.states?.suppressed && (!this.isSalePublished() || this.isItemCreated(item));
        });

        const hasSecret = { SECRETS: itemHasSecretOrder ? this.$t("secrets") : "" };
        let title = this.$t("supprimer_les_lots");
        let textContent = this.$t("vous_allez_supprimer_n_lots", [concernedItems.length]);
        let ok = this.$t("supprimer");
        let cancel = this.$t("fermer");

        if (itemsWithOrder.length || itemHasSecretOrder) {
            title = this.$t("suppression_des_ordres_d_achat", hasSecret);
            textContent = this.$t("attention_cette_action_entrainera_la_suppression_des_ordres_d_achat", hasSecret);
            ok = this.$t("oui");
            cancel = this.$t("non");
        }

        this.$mdDialog
            .show(this.$mdDialog.confirm().title(title).textContent(textContent).ok(ok).cancel(cancel))
            .then(() => this._patchItems(concernedItems, { states: { suppressed: true } }));
    }

    public togglePremium($event, saleStartAt, saleStarted?: boolean) {
        this.$mdDialog
            .show({
                targetEvent: $event,
                template: require("./toggle-premium-dialog.html"),
                fullscreen: this.$mdMedia("xs"),
                locals: { nbElemSelected: this.elems.length },
                controller: TogglePremiumDialogController,
                controllerAs: "togglePremiumCtrl"
            })
            .then(askPremium => {
                if (askPremium === undefined) {
                    return;
                }

                if ((isSaleInLessThan48hours(saleStartAt) || saleStarted) && askPremium === false) {
                    this.toaster.toastPremiumOnSaleStartedOrInLessThan48hours(saleStarted);
                    return;
                }

                const concernedItems = this.elems.filter(item =>
                    this.categoryService.checkPremiumCriteria(item) && askPremium
                        ? item.asked_for_premium === false
                        : item.asked_for_premium === true
                );
                const notConcernedItems = this.elems.filter(item => !this.categoryService.checkPremiumCriteria(item));

                const notConcernedItemIds = notConcernedItems
                    .filter(item => item.meta?.order_number?.primary)
                    .map(getOrderNbr)
                    .sort((a, b) =>
                        a.localeCompare(b, this.utils.getCurrentLang(), { numeric: true, sensitivity: "base" })
                    );

                if (!concernedItems.length && !notConcernedItems.length) {
                    return;
                }

                this._patchItems(concernedItems, { asked_for_premium: askPremium });
                const notNumberedUnpublishableItems = notConcernedItems.filter(
                    item => !item.meta?.order_number?.primary
                );

                const textContent = notConcernedItems.length
                    ? `<div>
                    <p class="bold">
                    ${
                        concernedItems.length
                            ? this.$t(askPremium ? "x_lots_marques_premium" : "x_lots_marques_non_premium", [
                                  concernedItems.length
                              ])
                            : ""
                    }
                    </p>
                    <div>
                        <span class="bold">
                            ${this.$t("x_lots_non_modifies_car_categorie_non_prenium", [notConcernedItems.length])}
                        </span>
                        <ul class="bullet-list">
                            <li>${this.$t("refusal_reasons.category")}</li>
                            <li>${this.$t("refusal_reasons.description")}</li>
                            <li>${this.$t("refusal_reasons.media")}</li>
                            <li>${this.$t("refusal_reasons.shipping")}</li>
                        </ul>
                        <span>
                            ${this.$t("liste_des_lots")}
                        </span>
                        <ul class="bullet-list">
                            ${notConcernedItemIds.length ? `<li> [${notConcernedItemIds.join(", ")}]</li>` : ""}
                            <li>
                                ${
                                    notNumberedUnpublishableItems.length && notConcernedItemIds.length
                                        ? this.$t("et_x_lots_non_numerotes", [notNumberedUnpublishableItems.length])
                                        : this.$t("x_lots_non_numerotes", [notNumberedUnpublishableItems.length])
                                }
                            </li>
                        </ul>
                    </div>
                </div>`
                    : `
                    <p class="bold">
                        ${this.$t(askPremium === true ? "x_lots_marques_premium" : "x_lots_marques_non_premium", [concernedItems.length])}
                    </p>`;

                this.$mdDialog.show({
                    template: `
                    <md-dialog aria-label="{{ ::'lots_premium' | translate }}" flex="40">
                        <md-toolbar class="md-accent">
                            <div class="md-toolbar-tools">
                                <h2 translate>lots_premium</h2>
                                <span flex></span>
                                <md-button
                                    aria-label="{{ ::'fermer_la_fenetre' | translate }}"
                                    class="md-icon-button"
                                    ng-click="recapPremiumCtrl.closeDialog()"
                                >
                                <md-icon md-svg-icon="close"></md-icon>
                            </md-button>
                            </div>
                        </md-toolbar>
                        <md-dialog-content class="md-dialog-content">
                            ${textContent}
                        </md-dialog-content>
                        <md-dialog-actions>
                            <md-button ng-click="recapPremiumCtrl.closeDialog()" class="md-primary">
                                <span translate>fermer</span>
                            </md-button>
                        </md-dialog-actions>
                    </md-dialog>`,
                    fullscreen: this.$mdMedia("xs"),
                    controller: RecapPremiumDialogController,
                    controllerAs: "recapPremiumCtrl"
                });
            });
    }

    private evaluatePossibleActions() {
        this.possibleActions = this.elems.reduce(
            (acc: MultiSelectActions, item: thor.ItemGet) => {
                if (!this.$rootScope.isRouteOnline || !this.isSalePublished() || this.isItemCreated(item)) {
                    acc.canBeCategorized = true;

                    if (
                        this.elems?.filter(i => !this.$rootScope.isRouteOnline || this.isItemCreated(i)).length > 1 &&
                        item.medias?.length
                    ) {
                        acc.canBeMerged = true;
                    }
                    acc.canBeSuppressed = !item.states?.suppressed;
                    acc.canBeUnSuppressed = item.states?.suppressed;
                }
                if (this.isSalePublished() && !this.isItemCreated(item)) {
                    acc.canBeHidden = !item.states?.hidden;
                    acc.canBeUnHidden = item.states?.hidden;
                }
                return acc;
            },
            {
                canBeCategorized: false,
                canBeMerged: false,
                canBeHidden: false,
                canBeUnHidden: false,
                canBeSuppressed: false,
                canBeUnSuppressed: false
            }
        );
    }

    private _patchItems(items, datas) {
        this.isBusy = true;

        const updateItem = item => {
            // Don't merge patched datas, cause they're already merged
            if (!datas.category_id) {
                merge(item, datas);
            }

            if (item.status === Enums.ItemStatus.Confirmed) {
                item.status = Enums.ItemStatus.Modified;
            }

            item.last_updated = new Date();
            return this.itemService.update(item.id, datas);
        };

        return this.$q
            .series(items, updateItem)
            .then(() => {
                // no check to translations status needed
                this.$rootScope.$broadcast(
                    this.$rootScope.isRouteOnline ? Events.OnlineItemUpdated : Events.ItemUpdated
                );
                this.$rootScope.$broadcast(Events.PostMessageToV2);
                this.evaluatePossibleActions();
            })
            .catch(error => {
                this.$log.error("Error on patch items in series", error);
                let message: string;
                switch (error?.data?.message) {
                    case ApiErrorMessage.DraftOnlineSalePublished:
                    case ApiErrorMessage.OnlineSaleStarted:
                        message = "mise_a_jour_impossible_la_vente_que_vous_souhaitez_modifier_est_deja_commencee";
                        break;
                    default:
                        message = "une_erreur_est_survenue";
                        break;
                }
                return this.toaster.show(this.$t(message), true);
            })
            .finally(() => {
                this.isBusy = false;
                this.cancel();
            });
    }

    private _fetchAllItemMedias(item) {
        return this.itemService.getMedias(item.id).then(medias => {
            item.medias = medias;
            item.media_count = medias.length;
        });
    }

    private _mergeItems(items) {
        const itemWithDescriptionIndex = items.findIndex(i => !!i.description);
        let baseItem;
        let mediasToAdd = [];

        if (itemWithDescriptionIndex !== -1) {
            baseItem = items[itemWithDescriptionIndex];
            delete items[itemWithDescriptionIndex];
        } else {
            baseItem = items.shift();
        }

        items.forEach(
            item =>
                (mediasToAdd = mediasToAdd.concat(
                    // Prevent attaching already existing media to destination item
                    item.medias.filter(media => !baseItem.medias.find(m => m.id === media.id))
                ))
        );

        // item medias are limited
        const maxAddedMedias = this.configService.items.maxImages - baseItem.medias.length;

        if (maxAddedMedias >= 0) {
            mediasToAdd = mediasToAdd.slice(0, maxAddedMedias);
        }

        let baseRank = baseItem.medias.length - 1;

        const mergeMediasPromises = mediasToAdd.map(media => {
            baseRank++;

            const datas = {
                media_id: media.id,
                type: MediaType.ItemPicture,
                rank: baseRank
            };

            return this.itemService.createMedia(baseItem.id, datas);
        });

        return this.$q.all(mergeMediasPromises).then(() => {
            baseItem.medias = baseItem.medias.concat(mediasToAdd);
            baseItem.media_count = baseItem.medias.length;
            baseItem.last_updated = new Date();

            if (baseItem.status === Enums.ItemStatus.Confirmed) {
                baseItem.status = Enums.ItemStatus.Modified;
            }
            return this._patchItems(items, { states: { suppressed: true } });
        });
    }

    private isSalePublished(): boolean {
        return this.elemType === MultiSelectType.SalePublished;
    }

    private isItemCreated(item): boolean {
        return item.status === Enums.ItemStatus.Created;
    }
}
